// The dancer's input as an append-only event log, and the count map derived from it.
//
// The log is the only thing the app stores about the count. It is never edited: a
// correction is a new event, and `undo` cancels the one before it. The map is a pure
// function of the beats from the tracker and the log (`deriveMap`), so replaying a log
// gives the same map, and an old log can be re-derived against a better tracker.
// Times are audio time in seconds.

import { medianInterval, mod } from './util.js';
import { beatIndexAt, tapResidue, type CountMap } from './grid.js';
import { PHRASE_BEATS, type ClaveDirection, type ClaveSetting } from './patterns.js';
import { halfBeatShift } from './tracker/align.js';
import { seedItems, seedTaps } from './seed.js';

/** One entry of the log. `t` is audio time in seconds, when it happened. */
export type CountEvent =
  /** A tap on count 1. Every `one` seeds the tracker (tempo, half-beat, which beat is 1). */
  | { readonly kind: 'one'; readonly t: number }
  /** A tap on count 1 later in the song: from the beat nearest `t` onward, that beat is count 1. Earlier phrases keep their count. */
  | { readonly kind: 'reanchor'; readonly t: number }
  /**
   * From `t` onward, move the count by one beat. +1 moves count 1 one beat later (the beat that was a 2 is now the 1);
   * -1 moves it one beat earlier (the beat that was the 1 is now a 2). The same sense as `shiftBeats`.
   */
  | { readonly kind: 'shift'; readonly t: number; readonly delta: -1 | 1 }
  /** From `t` onward, move the count by 4 beats (1 becomes 5, 5 becomes 1). */
  | { readonly kind: 'flip'; readonly t: number }
  /** Move the whole beat grid half a beat later. Global: `t` is only when it was pressed. It toggles: two of them cancel. */
  | { readonly kind: 'half'; readonly t: number }
  /** Set the clave direction, for the whole song. The last one wins. */
  | { readonly kind: 'clave'; readonly t: number; readonly direction: ClaveSetting }
  /** Start over: the count is derived from the events after the last `reset` in force. Undo can cancel it, which brings the earlier events back. */
  | { readonly kind: 'reset'; readonly t: number }
  /**
   * Cancel the most recent event that has not been cancelled. Repeated undos walk back through the log. With `tap` it cancels the
   * latest `drop` of that tap instead, whatever came after it ("put back" that one tap). Logs written before `tap` existed have none.
   */
  | { readonly kind: 'undo'; readonly t: number; readonly tap?: string }
  /**
   * Take one tap out of the count, whatever happened after it: `tap` is its `tapId` (a `one` or a `reanchor`). Only a tap made before the
   * drop and still in force is removed; a drop of a tap that a `reset` already removed does nothing. `t` is when it was dropped.
   * An `undo` cancels it like any event, which puts the tap back. Added after S4b: older logs hold none and derive the same map.
   */
  | { readonly kind: 'drop'; readonly t: number; readonly tap: string }
  /**
   * S9e: the count found from the audio alone (`proposeCount`, run by the analysis worker). It carries the proposal, so the count is still derived from the log and nothing else: with fewer
   * than two seed taps in force it is the seed (its tempo level and its "1", see `seedOf`). The dancer's own taps override it. `t` is 0. It is droppable like a tap (`autoId`): Review's Remove.
   */
  | { readonly kind: 'auto'; readonly t: number; readonly proposal: AutoProposal }
  /** S9e: "Yes": the dancer says the automatic count is right. Changes no count; it ends the question (`autoAwaiting`). */
  | { readonly kind: 'confirm'; readonly t: number }
  /**
   * S9e: halve or double the count's speed ("Too fast", "Too slow"), for the whole song (like `half`: `t` is only when it was pressed). Net halvings and doublings in force are summed and kept to -1 or +1:
   * -1 counts every second beat (the beats on the parity of the 1, so a phrase lasts two of the earlier ones), +1 puts a count on every midpoint between beats (a phrase lasts half as long).
   */
  | { readonly kind: 'level'; readonly t: number; readonly change: 'halve' | 'double' };

export type EventKind = CountEvent['kind'];

/** A decision's word: how sure the audio's decision was (`proposeCount`'s `Confidence`). */
export type Word = 'sure' | 'likely' | 'unsure';

/**
 * What `proposeCount` decided, as stored in an `auto` event. Times are seconds of the song.
 *  - `bpm`: the tempo level: the dancer's beats per minute the pulse tracker is centred on (the chosen candidate of the level search). The tracker re-runs from this and reproduces the lattice.
 *  - `anchor`: the lattice's anchor: the first pulse at or after 5 s, where the models start reading (the models were fitted on lattices from 5 s).
 *  - `one`: the proposed count 1: the first beat at or after the anchor that the proposal counts as 1. It is a beat of the tracker's lattice.
 *  - `words`, `margins`: the decision for the tempo level, the beats against the "&"s, the odd counts, the bar and "1" against "5": its word and its margin (nats; for "1", -ln p of the phrase evidence).
 *  - `evidence`: what decided "1": the strongest spectral changes counted on the chosen candidate and on the other, and the binomial p.
 *  - `levels`: the candidate tempo levels, best first, with their scores. `direction`: the clave direction, only when the model named one.
 */
export interface AutoProposal {
  readonly bpm: number;
  readonly anchor: number;
  readonly one: number;
  readonly words: { readonly tempo: Word; readonly beat: Word; readonly odd: Word; readonly bar: Word; readonly one: Word };
  readonly margins: { readonly tempo: number; readonly beat: number; readonly odd: number; readonly bar: number; readonly one: number };
  readonly evidence: { readonly chosen: number; readonly other: number; readonly events: number; readonly boundaries: number; readonly p: number };
  readonly levels: readonly { readonly bpm: number; readonly score: number }[];
  readonly direction: ClaveDirection | 'none';
}

/**
 * Every event not cancelled, in log order, `reset` included: every `undo` removes the most recent event still in force
 * (a `reset` too), and is itself dropped. An undo with nothing before it does nothing. `canUndo` is whether this is not empty.
 */
export function eventsInForce(events: readonly CountEvent[]): CountEvent[] {
  const stack: CountEvent[] = [];
  for (const e of events) {
    if (e.kind !== 'undo') stack.push(e);
    else if (e.tap === undefined) stack.pop();
    else {
      for (let i = stack.length - 1; i >= 0; i--) {
        const s = stack[i]!;
        if (s.kind === 'drop' && s.tap === e.tap) { stack.splice(i, 1); break; }
      }
    }
  }
  return stack;
}

/** Is there anything for Undo to cancel? (A `reset` counts: undoing it brings back the events before it.) */
export function canUndo(events: readonly CountEvent[]): boolean {
  return eventsInForce(events).length > 0;
}

/** A tap: the events the dancer makes by tapping on count 1. */
export type CountTap = Extract<CountEvent, { readonly kind: 'one' | 'reanchor' }>;

export const isTap = (e: CountEvent): e is CountTap => e.kind === 'one' || e.kind === 'reanchor';

/**
 * The identity of a tap: its kind and its stored time, which never change once it is logged (the log is append-only, and a tap is
 * stored already corrected by the calibration). Two taps cannot share one in practice: the engine refuses a tap within 250 ms of another.
 * Old logs have no ids, so identity is derived from the event and every old tap already has one. Null for events that are not taps.
 */
export function tapId(e: CountEvent): string | null {
  return isTap(e) ? `${e.kind}@${e.t}` : null;
}

/** The identity of the automatic proposal, for `drop` and `undo` (S9e): one per log is in force at a time, and its `t` is 0. */
export const autoId = (e: Extract<CountEvent, { readonly kind: 'auto' }>): string => `auto@${e.t}`;

/** The identity a `drop` names: a tap's (`tapId`) or an automatic proposal's (`autoId`); null for the other events. */
export function droppableId(e: CountEvent): string | null {
  return e.kind === 'auto' ? autoId(e) : tapId(e);
}

/** A tap (or the automatic proposal) that a `drop` in force removed. */
export interface DroppedTap {
  readonly id: string;
  readonly t: number;
  readonly kind: 'one' | 'reanchor' | 'auto';
}

/** Events in force after the last `reset`: the ones the count is derived from (with their drops, in log order), and the taps those drops removed (oldest drop first). */
function resolve(events: readonly CountEvent[]): { live: CountEvent[]; dropped: DroppedTap[] } {
  const stack = eventsInForce(events);
  let from = 0;
  stack.forEach((e, i) => { if (e.kind === 'reset') from = i + 1; });
  let live: CountEvent[] = [];
  const dropped: DroppedTap[] = [];
  for (const e of from ? stack.slice(from) : stack) {
    if (e.kind !== 'drop') { live.push(e); continue; }
    const hit = live.filter((x) => (isTap(x) || x.kind === 'auto') && droppableId(x) === e.tap); // only taps (or the proposal) made before the drop, and not removed by a reset
    if (hit.length === 0) continue;
    live = live.filter((x) => !hit.includes(x));
    for (const x of hit) dropped.push({ id: e.tap, t: x.t, kind: x.kind as DroppedTap['kind'] });
  }
  return { live, dropped };
}

/**
 * The events the count is derived from: those in force after the last `reset`, in log order, without the taps a `drop` removed
 * (the drops themselves are used up and not listed).
 */
export function activeEvents(events: readonly CountEvent[]): CountEvent[] {
  return resolve(events).live;
}

/** The taps in force: every `one` and `reanchor` the count is derived from, in the order they were made. */
export function liveTaps(events: readonly CountEvent[]): CountTap[] {
  return resolve(events).live.filter(isTap);
}

/** The taps removed by a `drop` in force, in the order they were dropped (the last is the one "Put back" brings back). */
export function droppedTaps(events: readonly CountEvent[]): DroppedTap[] {
  return resolve(events).dropped;
}

/** The identity of the tap made last that is still in force (what "Undo tap" removes), or null. Corrections made after it do not matter. */
export function lastTapId(events: readonly CountEvent[]): string | null {
  const taps = liveTaps(events);
  return taps.length ? tapId(taps[taps.length - 1]!) : null;
}

/** The automatic proposal in force (the latest `auto` event after the last reset that no `drop` or `undo` removed), or null. */
export function proposalOf(events: readonly CountEvent[]): AutoProposal | null {
  let found: AutoProposal | null = null;
  for (const e of activeEvents(events)) if (e.kind === 'auto') found = e.proposal;
  return found;
}

/**
 * What the count is seeded from (S9e). Two or more seed taps in force (`oneTaps`): the dancer's taps, which override the proposal wherever both are in the log. Otherwise the
 * automatic proposal in force, as a virtual tap on its "1" at its tempo level. Otherwise nothing: the player asks for taps.
 */
export type Seed =
  | { readonly kind: 'taps'; readonly taps: readonly number[] }
  | { readonly kind: 'auto'; readonly proposal: AutoProposal }
  | { readonly kind: 'none' };

export function seedOf(events: readonly CountEvent[]): Seed {
  const taps = oneTaps(events);
  if (taps.length >= 2) return { kind: 'taps', taps };
  const proposal = proposalOf(events);
  return proposal ? { kind: 'auto', proposal } : { kind: 'none' };
}

/** There is no count yet (neither two seed taps nor a proposal in force): the player asks for taps. */
export function needsTaps(events: readonly CountEvent[]): boolean {
  return seedOf(events).kind === 'none';
}

/**
 * Where the count in force comes from (the taps bar says so):
 *  - `auto`: the proposal, with whatever fixes and answers were made to it;
 *  - `overridden`: the proposal and then a tap by the dancer (a `reanchor`: "Count set by you");
 *  - `taps`: the dancer's seed taps; `none`: no count.
 */
export type CountOrigin = 'none' | 'auto' | 'overridden' | 'taps';

export function countOrigin(events: readonly CountEvent[]): CountOrigin {
  const seed = seedOf(events);
  if (seed.kind === 'none') return 'none';
  if (seed.kind === 'taps') return 'taps';
  return activeEvents(events).some((e) => e.kind === 'reanchor' || e.kind === 'one') ? 'overridden' : 'auto';
}

/** The proposal is on the screen with its question: in force, not confirmed (`confirm`) and not overridden by a tap. The confirm row replaces the fix row while this holds. */
export function autoAwaiting(events: readonly CountEvent[]): boolean {
  return countOrigin(events) === 'auto' && !activeEvents(events).some((e) => e.kind === 'confirm');
}

/** The net speed level in force (S9e): doublings minus halvings of the `level` events, kept to -1 (every second beat), 0 or +1 (a count on every midpoint). */
export function levelNet(events: readonly CountEvent[]): -1 | 0 | 1 {
  let n = 0;
  for (const e of activeEvents(events)) if (e.kind === 'level') n = Math.max(-1, Math.min(1, n + (e.change === 'double' ? 1 : -1)));
  return n as -1 | 0 | 1;
}

/**
 * The taps to give the tracker: the `one` events in force, as the seed rule keeps them (seed.ts: a double tap is one tap, and a
 * tap that is not a whole number of plausible 8-counts after the others starts the seed again). Times are as stored: the device's
 * calibration was applied when each tap was made.
 */
export function oneTaps(events: readonly CountEvent[]): number[] {
  return seedTaps(activeEvents(events).filter((e) => e.kind === 'one').map((e) => e.t));
}

export interface DeriveOptions {
  /**
   * A further latency to take off every tap, in seconds. The app stores each tap already corrected by the calibration in force when
   * it was made, so it passes none; the bench and old logs use it. `one` and `reanchor` are taps; `shift` and `flip` are not.
   */
  readonly expectedTapOffsetSec?: number;
  /**
   * 0 or 1: the beats at this parity of index in `beats` are the odd counts (1, 3, 5, 7), as the phase model found them (S9c, `Track.oddParity`); null or absent: not known.
   * The 1 is then the odd beat nearest the taps. Ignored while an odd number of `half` events is in force: the dancer moved the grid, and the taps decide.
   */
  readonly oddParity?: 0 | 1 | null;
}

/** A count map with explicit beats and a count for every one of them. Works anywhere a `CountMap` does. */
export interface DerivedMap extends CountMap {
  /** Beat times in seconds, ascending: the tracker's beats, moved half a beat if an odd number of `half` events is in force. */
  readonly beats: readonly number[];
  /** Count (1..8) of each beat, same length as `beats`. */
  readonly counts: readonly number[];
  /** Index in `beats` of the first beat that is count 1 (0 if none is). */
  readonly anchor: number;
  readonly clave: ClaveSetting;
}

/**
 * The count map for these beats under this log. Pure; the inputs are not changed.
 *
 *  0. (S9e) With fewer than two seed taps in force and an `auto` proposal in force, the proposal's "1" is the one tap (a virtual one, at its own time). The net `level`
 *     events in force then halve or double the beats (`levelGrid`) before anything else: the count is on that grid, and `half` moves it half of one of its beats.
 *  1. With none, the first beat is count 1. Otherwise the `one` events in force vote for which beat of the grid as shown (after step 2) is
 *     count 1 (`tapResidue`).
 *  2. An odd number of `half` events moves the grid half a beat later. The 1 is then found on that grid (S4a), not on the tracker's.
 *  3. The other events apply in log order to the beats at and after a time, so a later event overrides an earlier one only
 *     where they overlap and the phrases before it are untouched: `reanchor` makes the beat nearest `t` count 1 and counts on
 *     from it; `shift` and `flip` move the count from the beat sounding at `t` onward. All of them are found on the grid as shown
 *     (after `half`).
 *  4. The last `clave` event in force sets the clave.
 */
export function deriveMap(beats: readonly number[], events: readonly CountEvent[], options: DeriveOptions = {}): DerivedMap {
  if (beats.length < 2) throw new RangeError('deriveMap needs at least two beats');
  const offset = options.expectedTapOffsetSec ?? 0;
  const active = activeEvents(events);

  // The seed: the dancer's taps, or the automatic proposal's "1" as a virtual tap on a beat (no calibration offset: it is not a human tap).
  const seed = seedOf(events);
  const ones = seed.kind === 'auto' ? [seed.proposal.one] : oneTaps(events).map((t) => t - offset);
  const net = levelNet(events);
  const leveled = net !== 0 && ones.length ? levelGrid(beats, net, ones[0]!) : beats;
  const halves = active.filter((e) => e.kind === 'half').length;
  const grid = halves % 2 ? halfBeatShift(leveled) : [...leveled];
  const n = grid.length;
  if (seed.kind === 'auto' && halves % 2) {
    // The proposal's "1" is a beat of the grid as tracked; "half a beat later" moves that same beat, so the virtual tap goes with it (on the old time it would sit exactly between two moved beats).
    const k = Math.round(beatIndexAt({ origin: leveled[0]!, beat: medianInterval(leveled), beats: leveled, anchor: 0 }, ones[0]!));
    ones[0] = grid[Math.min(n - 1, Math.max(0, k))]!;
  }
  const residue = ones.length ? tapResidue(grid, ones, halves % 2 || leveled !== beats ? null : options.oddParity ?? null) : 0;
  const counts = Array.from({ length: n }, (_, i) => mod(i - residue, PHRASE_BEATS) + 1);
  const lattice: CountMap = { origin: grid[0]!, beat: medianInterval(grid), beats: grid, anchor: 0 };
  const clamp = (i: number) => Math.min(n - 1, Math.max(0, i));

  let clave: ClaveSetting = 'unknown';
  for (const e of active) {
    if (e.kind === 'reanchor') {
      const j = clamp(Math.round(beatIndexAt(lattice, e.t - offset)));
      for (let i = j; i < n; i++) counts[i] = mod(i - j, PHRASE_BEATS) + 1;
    } else if (e.kind === 'shift' || e.kind === 'flip') {
      const by = e.kind === 'flip' ? PHRASE_BEATS / 2 : e.delta;
      for (let i = clamp(Math.floor(beatIndexAt(lattice, e.t))); i < n; i++) counts[i] = mod(counts[i]! - 1 - by, PHRASE_BEATS) + 1;
    } else if (e.kind === 'clave') {
      clave = e.direction;
    }
  }

  const anchor = Math.max(0, counts.indexOf(1));
  return { origin: grid[anchor]!, beat: lattice.beat, beats: grid, anchor, counts, clave };
}

/**
 * The beats at the speed `net` of `level` events puts them (S9e). `+1`: a beat on every midpoint between two beats as well (the last one extrapolates), so the old beats are at the even
 * positions. `-1`: every second beat, the ones on the parity of the beat nearest `oneT` (so the 1 and every odd count of the earlier count stay beats). A grid too short to halve is returned as it is.
 */
export function levelGrid(beats: readonly number[], net: -1 | 1, oneT: number): number[] {
  const n = beats.length;
  if (net === 1) {
    const out: number[] = [];
    for (let i = 0; i + 1 < n; i++) out.push(beats[i]!, (beats[i]! + beats[i + 1]!) / 2);
    out.push(beats[n - 1]!, beats[n - 1]! + (beats[n - 1]! - beats[n - 2]!) / 2);
    return out;
  }
  const near = Math.round(beatIndexAt({ origin: beats[0]!, beat: medianInterval(beats), beats, anchor: 0 }, oneT));
  const keep = mod(near, 2);
  const out = beats.filter((_, i) => i % 2 === keep);
  return out.length >= 2 ? out : [...beats];
}

/**
 * What a tap did, for the review of taps:
 *  - `set`: a `one` tap that seeds the count (the seed rule kept it: seed.ts);
 *  - `moved`: a later tap (`reanchor`) that changed the count from its beat on, because that beat was not count 1 when it was made;
 *  - `agreed`: a later tap whose beat already was count 1;
 *  - `unused`: it changed nothing and agreed with nothing: a `one` tap the seed rule left out (a later tap started the seed again), or any tap while there is no count to move (neither two seed taps nor a proposal);
 *  - `proposed` (S9e): the row of the automatic proposal (`kind` `auto`), always first; its `t` is the time of its first "1".
 */
export type TapEffect = 'set' | 'moved' | 'agreed' | 'unused' | 'proposed';

/** One live tap in the review: its identity, stored time, kind and effect. */
export interface ReviewTap {
  readonly id: string;
  readonly t: number;
  readonly kind: 'one' | 'reanchor' | 'auto';
  readonly effect: TapEffect;
}

/**
 * The taps in force, in song order, each with what it did. `beats` are the beats the count is on (null: none yet). A `reanchor`
 * is `moved` or `agreed` by the count the events before it gave at its beat (the state when it was made), so the labels do not depend on
 * what the dancer corrected afterwards. Pure; the inputs are not changed. Dropped taps are not listed (`droppedTaps`).
 */
export function reviewTaps(beats: readonly number[] | null, events: readonly CountEvent[], options: DeriveOptions = {}): ReviewTap[] {
  const live = activeEvents(events);
  const seed = new Set<CountEvent>(seedItems(live.filter((e) => e.kind === 'one'), (e) => e.t));
  const counted = beats !== null && beats.length >= 2 && seedOf(live).kind !== 'none';
  const offset = options.expectedTapOffsetSec ?? 0;
  const rows: ReviewTap[] = [];
  live.forEach((e, k) => {
    if (e.kind === 'auto') {
      rows.push({ id: autoId(e), t: e.proposal.one, kind: 'auto', effect: 'proposed' });
    } else if (e.kind === 'one') {
      rows.push({ id: tapId(e)!, t: e.t, kind: 'one', effect: seed.has(e) ? 'set' : 'unused' });
    } else if (e.kind === 'reanchor') {
      let effect: TapEffect = 'unused';
      if (counted) {
        const before = deriveMap(beats, live.slice(0, k), options);
        const j = Math.min(before.beats.length - 1, Math.max(0, Math.round(beatIndexAt(before, e.t - offset))));
        effect = before.counts[j] === 1 ? 'agreed' : 'moved';
      }
      rows.push({ id: tapId(e)!, t: e.t, kind: 'reanchor', effect });
    }
  });
  return rows.sort((a, b) => (a.kind === 'auto' ? 0 : 1) - (b.kind === 'auto' ? 0 : 1) || a.t - b.t); // the proposal first, then the taps in song order
}
